import { useState } from 'react';
import { Badge, Collapse, Radio } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Colors } from '../../styles/Colors';
import { GlobalStyleAttributes } from '../../styles/GlobalStyles';
import CustomButtonV2 from './CustomButtonV2';
import {isRTL} from "../../services/i18n";

/**
 * CustomCollapse — a single selectable collapse panel.
 *
 * The item can optionally show a Badge.Ribbon (e.g. "X spots remaining").
 * When selected it gets a highlighted body background and a checked radio.
 *
 * Props:
 *  - item          {{
 *                    key: string,
 *                    label: ReactNode,
 *                    children: ReactNode,
 *                    ribbon?: { text: string, color?: string }
 *                  }}
 *  - isExpanded    {boolean}   Whether the collapse panel is open.
 *  - isSelected    {boolean}   Whether the radio button is checked.
 *  - isBookedUser  {boolean}   When true the row is disabled (no hover/click/radio)
 *                              but the ribbon badge remains fully visible.
 *  - onExpand      {function}  Called with (string|null) when the +/- toggle is clicked.
 *  - onSelect      {function}  Called with (string|null) when the radio is clicked.
 */
const CustomCollapse = (props) => {
    const { item, isExpanded, isSelected, isBookedUser, onExpand, onSelect } = props
    const [isHovered, setIsHovered] = useState(false);
    const ribbon = item.ribbon;

    const ExpandIcon = ({ isActive }) => (
        <CustomButtonV2
            type={'link'}
            iconProps={{ iconColor: Colors.newDesign.mainText, iconHoverColor: Colors.newDesign.mainBlue, iconSize: '10px' }}
            override={({ color }) => (
                <FontAwesomeIcon
                    icon={isActive ? light('minus') : light('plus')}
                    style={{
                        border: '1px solid #E0E0E0',
                        borderRadius: '3px',
                        width: '18px',
                        height: '16px',
                        boxSizing: 'border-box',
                        color: color,
                    }}
                />
            )}
        />
    );

    return (
        <div
            onMouseEnter={() => !isBookedUser && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ backgroundColor: isBookedUser ? Colors.newDesign.white : (isHovered ? Colors.newDesign.menuBar : (isSelected ? Colors.newDesign.lightBlue : Colors.newDesign.white)) }}
        >
        <Badge.Ribbon
            text={ribbon?.text ? (
                <span style={{ fontSize: GlobalStyleAttributes.fonts.infoText, fontFamily: 'Ploni' }}>
                    {ribbon.text}
                </span>
            ) : null}
            color={ribbon?.color ?? Colors.newDesign.lightGreen}
            placement={'end'}
            className={`hug-swap-ribbon${ribbon?.isDisabled ? ' hug-swap-ribbon--disabled' : ''}`}
            style={{ display: ribbon?.text ? undefined : 'none' }}
        >
            <div style={{ opacity: isBookedUser ? 0.45 : 1, pointerEvents: isBookedUser ? 'none' : undefined }}>
            <Collapse
            accordion={false}
            activeKey={isExpanded ? [item.key] : []}
            onChange={(keys) => onExpand(keys.length ? item.key : null)}
            expandIcon={({ isActive }) => <ExpandIcon isActive={isActive} />}
            expandIconPlacement={'start'}
            bordered={false}
            style={{padding: '8px 0px'}}
            items={[{
                key: item.key,
                label: (
                    <div
                        onClick={() => onSelect(item.key)}
                        style={{
                            cursor: isSelected ? 'default' : 'pointer',
                            paddingInlineEnd: ribbon?.text ? '8rem' : undefined,
                        }}
                    >
                        {item.label}
                    </div>
                ),
                children: item.children,
                showArrow: true,
                collapsible: 'icon',
                extra: (
                    <Radio
                        checked={isSelected}
                        onChange={(e) => { e.stopPropagation(); onSelect(item.key); }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ flexShrink: 0, marginRight: '0.5rem', position: 'relative', right: !isRTL() && '850px', left: isRTL() && '850px' }}
                    />
                ),
            }]}
        />
            </div>

        </Badge.Ribbon>
        </div>
    );
};

export default CustomCollapse;
